import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import UserLayout from '../../layouts/UserLayout';
import { Outlet } from 'react-router-dom';
import { useLanguage } from '../../context/LanguageContext';
import { useAuth } from '../../context/AuthContext';
import { useBetting } from '../../context/BettingContext';

import { BetSlip, MobileBetSlipFAB } from '../../features/betting/BetSlip';
import { Plus, ChevronRight } from 'lucide-react';

const UserApp = () => {
    const { t } = useLanguage();
    const navigate = useNavigate();

    // Context Data
    const { user, userProfile, openLogin } = useAuth();
    const { betSlip, setBetSlip, placeBet } = useBetting();
    // LiveEvents context is used deeper in the tree now (BettingLobby)

    // Removed dead showProfile state
    const [showBalance, setShowBalance] = useState(true);
    const [showMobileSlip, setShowMobileSlip] = useState(false);
    const [localActiveCurrency, setLocalActiveCurrency] = useState('BS');

    // Sync currency with user profile if available, else default to BS
    useEffect(() => {
        if (userProfile?.activeCurrency) setLocalActiveCurrency(userProfile.activeCurrency);
    }, [userProfile]);

    const activeCurrency = localActiveCurrency;
    const setActiveCurrency = setLocalActiveCurrency; // In real app, might want to save to DB

    const wallets = {
        BS: { real: userProfile?.balanceBs || 0, bonus: userProfile?.bonusBalance || 0, symbol: 'Bs', name: 'Bolivianos' },
        USDT: { real: userProfile?.balanceUsdt || 0, bonus: 0, symbol: '₮', name: 'Tether' },
        BONUS: { real: userProfile?.balanceBs || 0, bonus: userProfile?.bonusBalance || 0, symbol: 'Bs', name: 'Bono (Bs)' }
    };

    const normalizedCurrency = activeCurrency === 'BOB' ? 'BS' : activeCurrency;
    const currentWallet = wallets[normalizedCurrency] || wallets['BS'];

    // View Rendering is now handled by Router Outlet


    return (
        <UserLayout
            activeTab={window.location.pathname.split('/')[1] || 'home'}
            setActiveTab={(tab, state) => {
                if (tab === 'home') navigate('/', { state });
                else navigate(`/${tab}`, { state });
            }}
            userProfile={userProfile}
            activeCurrency={normalizedCurrency}
            user={user}
            onLoginRequest={openLogin}
        >
            {/* Header Mobile */}
            <header className="lg:hidden px-4 py-3 flex justify-between items-center border-b border-[var(--border-color)] z-[100] sticky top-0 bg-[#171717] shrink-0">
                <div className="flex items-center gap-3">
                    {user ? (
                        <>
                            <div
                                onClick={() => navigate('/profile')}
                                className="relative cursor-pointer group active:scale-95 transition-transform"
                            >
                                <div className="w-10 h-10 rounded-xl bg-[var(--bg-secondary)] border-[1.5px] border-[var(--border-color)] overflow-hidden group-hover:border-[#F59E0B] transition-all duration-300 shadow-xl">
                                    <img src={`https://api.dicebear.com/7.x/avataaars/svg?seed=${user?.email || 'Felix'}`} alt="Profile" className="w-full h-full object-cover" />
                                </div>
                                <div className="absolute -bottom-1 -right-1 bg-[#F59E0B] rounded-md p-1 shadow-lg ring-2 ring-neutral-900">
                                    <Plus size={8} className="text-black" strokeWidth={5} />
                                </div>
                            </div>
                            <div className="flex flex-col justify-center gap-0.5 mt-1">
                                <span className="text-[9px] text-[var(--text-primary)] font-black uppercase tracking-widest leading-none">HOLA, {userProfile?.firstName?.split(' ')[0] || user.email?.split('@')[0] || 'USUARIO'}</span>
                                <img src="/assets/branding/bannermovilOficial.png" alt="Banner" className="h-3 object-contain" />
                            </div>
                        </>
                    ) : (
                        <div className="flex items-center gap-2">
                            <img src="/assets/branding/bannerLogoOficial.png" alt="PrimeApuesta" className="h-9 object-contain" />
                        </div>
                    )}
                </div>

                <div className="flex items-center bg-[var(--bg-primary)]/60 rounded-xl p-0.5 border border-[var(--border-color)] shadow-inner">
                    {user ? (
                        <>
                            <div className="flex flex-col items-end px-2 group">
                                <button
                                    onClick={() => setActiveCurrency(prev => prev === 'USDT' ? 'BS' : 'USDT')}
                                    className="text-[8px] text-[#F59E0B] font-black uppercase tracking-[0.1em] flex items-center gap-1 hover:text-white transition-colors cursor-pointer bg-neutral-800/50 px-1.5 rounded-sm"
                                    title="Cambiar Moneda"
                                >
                                    {normalizedCurrency} <span className="text-[7px]">⇄</span>
                                </button>
                                <span 
                                    onClick={() => setShowBalance(!showBalance)}
                                    className="text-xs font-black text-[var(--text-primary)] font-mono leading-none tracking-tight cursor-pointer hover:opacity-80 mt-0.5"
                                >
                                    {showBalance ? (
                                        normalizedCurrency === 'USDT'
                                            ? `₮${Number(userProfile?.balanceUsdt || 0).toLocaleString('es-ES', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
                                            : `Bs ${(Number(userProfile?.balanceBs || 0) + Number(userProfile?.bonusBalance || 0)).toLocaleString('es-ES', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
                                    ) : '••••••'}
                                </span>
                            </div>
                            <button
                                onClick={() => navigate('/deposit')}
                                className="bg-[#F59E0B] hover:bg-[#F59E0B]/90 text-black text-[10px] font-black uppercase px-4 py-2.5 rounded-lg shadow-lg shadow-[#F59E0B]/10 transition-all active:scale-95 flex items-center gap-1 h-full"
                            >
                                {t('nav.deposit')}
                            </button>
                        </>
                    ) : (
                        <button
                            onClick={openLogin}
                            className="bg-[#F59E0B] hover:bg-[#F59E0B]/90 text-black text-[10px] font-black uppercase px-4 py-2.5 rounded-lg shadow-lg shadow-[#F59E0B]/10 transition-all active:scale-95 flex items-center gap-1 h-full"
                        >
                            Iniciar Sesión
                        </button>
                    )}
                </div>
            </header>

            <main className="min-h-full">
                <Outlet context={{
                    activeCurrency: normalizedCurrency,
                    wallets,
                    openLogin,
                    user,
                    userProfile
                }} />
            </main>



            <div className="desktop-only">
                <BetSlip
                    betSlip={betSlip}
                    setBetSlip={setBetSlip}
                    onPlaceBet={placeBet}
                    user={user}
                    userProfile={userProfile}
                    activeCurrency={activeCurrency}
                />
            </div>

            {betSlip.size > 0 && !showMobileSlip && (
                <MobileBetSlipFAB
                    count={betSlip.size}
                    odds={Array.from(betSlip.values()).reduce((a, b) => a * b.oddValue, 1).toFixed(2)}
                    onClick={() => setShowMobileSlip(true)}
                />
            )}

            {showMobileSlip && (
                <div className="fixed inset-0 z-[100] lg:hidden flex flex-col justify-end">
                    <div
                        className="absolute inset-0 bg-black/80 backdrop-blur-sm transition-opacity duration-300 opacity-100"
                        onClick={() => setShowMobileSlip(false)}
                    ></div>
                    <div className="relative w-full h-[88vh] bg-[#171717] rounded-t-[2.5rem] shadow-2xl flex flex-col overflow-hidden transition-transform duration-300 ease-out translate-y-0 will-change-transform">
                        <div className="w-full bg-[#171717] py-4 flex justify-center shrink-0 border-b border-white/5" onClick={() => setShowMobileSlip(false)}>
                            <div className="w-16 h-1.5 bg-neutral-700 rounded-full"></div>
                        </div>
                        <div className="flex-1 w-full overflow-hidden">
                            <BetSlip
                                betSlip={betSlip}
                                setBetSlip={setBetSlip}
                                onPlaceBet={async (amt) => {
                                    await placeBet(amt);
                                    setShowMobileSlip(false);
                                }}
                                user={user}
                                userProfile={userProfile}
                                activeCurrency={activeCurrency}
                                onCloseMobile={() => setShowMobileSlip(false)}
                            />
                        </div>
                    </div>
                </div>
            )}
        </UserLayout>
    );
};

export default UserApp;
